import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import EmployeesComponent from "./EmployeesComponent";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { DEPARTMENTS_QUERY, EMPLOYEES_QUERY, PARTNER_STATES_QUERY, PERSONS_QUERY, PERSON_QUERY } from "./queries";
import { emptyArray } from "constants/variousTypes";
import EmployeeModalComponent from "./EmployeeModalComponent";
import { useApolloClient, useMutation } from "@apollo/client";
import { useChangeTrackingState } from "@totalsoft/change-tracking-react";
import { DELETE_EMPLOYEE, SAVE_EMPLOYEE } from "./mutations";
import { required, shape, concatFailure } from "@totalsoft/pure-validations";
import { dateIsLessThanOrEqualToCurrentDate } from "utils/pure-validation/primitiveValidators";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { isDirty as checkDirty } from "@totalsoft/change-tracking";
import { useSimpleError } from "hooks/apolloWrappers";

export const employeeValidator = shape({
  employeeName: required,
  hireDate: [required, dateIsLessThanOrEqualToCurrentDate] |> concatFailure,
  badge: required,
  employeeStateId: required
});

const emptyEmployee = {
  employeeId: 0,
  employeeName: "",
  hireDate: null,
  badge: null,
  reportsToId: null,
  reportsToName: null,
  address: null,
  employeeStateId: null,
  employeeStateName: null,
  departmentId: null,
  departmentName: null,
  person: {
    partnerId: null,
    lastName: null,
    firstName: null,
    genderId: null,
    genderCode: null,
    birthDate: null,
    maritalStatus: null,
    idSeriesAndNumber: null,
    idIssuedBy: null,
    citizenshipId: null,
    citizenshipName: null,
    email: null,
    positionId: null,
    positionName: null
  }
};

export const EmployeesContainer = ({ partnerId, hasWritePermission }) => {
  const apolloClient = useApolloClient();

  const [employees, setEmployees] = useState([]);
  const [employee, dirtyInfo, setEmployee, resetEmployeeState] = useChangeTrackingState({});
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const showError = useSimpleError();

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const [validation, validate, resetValidation] = useDirtyFieldValidation(employeeValidator);

  const employeeIds = useMemo(() => employees.map(employee => employee.employeeId), [employees]);

  const { data: employeesData, loading: employeesLoading } = useQueryWithSimpleErrorHandling(EMPLOYEES_QUERY, {
    variables: { partnerId: partnerId }
  });

  const loadPerson = useCallback(
    async partnerId => {
      const { data } = await apolloClient.query({
        query: PERSON_QUERY,
        variables: { partnerId }
      });
      return data?.person || {};
    },
    [apolloClient]
  );

  const loadPersons = useCallback(
    async search => {
      const { data } = await apolloClient.query({
        query: PERSONS_QUERY,
        variables: { search },
        fetchPolicy: "network-only"
      });
      return data?.persons.filter(person => employeeIds.indexOf(person.id) === -1) || [];
    },
    [apolloClient, employeeIds]
  );

  const loadPartnerStates = useCallback(async () => {
    const { data } = await apolloClient.query({
      query: PARTNER_STATES_QUERY
    });
    return data?.partnerStates || [];
  }, [apolloClient]);

  const loadDepartments = useCallback(async () => {
    const { data } = await apolloClient.query({
      query: DEPARTMENTS_QUERY
    });
    return data?.departments || [];
  }, [apolloClient]);

  const [saveEmployee] = useMutation(SAVE_EMPLOYEE, {
    onError: error => {
      showError(error);
      setSaving(false);
    },
    onCompleted: async () => {
      const newEmployees = [...employees];
      const employeeIndex = employees.findIndex(item => item.employeeId === employee.employeeId);
      if (employeeIndex !== -1) {
        newEmployees[employeeIndex] = employee;
      } else {
        const newEmployee = { ...employee };
        newEmployee.employeeId = employee.person.partnerId;
        newEmployees.push(newEmployee);
        setEmployee(newEmployee);
      }
      setEmployees(newEmployees);
      setShowModal(false);
      setSaving(false);
      resetValidation();
    }
  });

  const [deleteEmployee] = useMutation(DELETE_EMPLOYEE, {
    onError: error => {
      showError(error);
      setSaving(false);
    },
    onCompleted: async () => {
      const employeeIndex = employees.findIndex(item => item.employeeId === employee.employeeId);
      if (employeeIndex !== -1) {
        const newEmployees = [...employees];
        newEmployees.splice(employeeIndex, 1);
        setEmployees(newEmployees);
        setEmployee({});
      }
      setSaving(false);
    }
  });

  useEffect(() => {
    if (!employeesLoading) setEmployees(employeesData?.employees || emptyArray);
  }, [employeesData, employeesLoading, setEmployees]);

  const handlePropertyChange = useCallback(
    async event => {
      const updatedEmployee = { ...employee };

      if (event.employeeId) {
        const person = await loadPerson(event.employeeId);
        updatedEmployee.person = person;
      }

      Object.keys(event).forEach(item => {
        if (item !== "employeeId") {
          updatedEmployee[item] = event[item];
        }
      });

      setEmployee(updatedEmployee);
      validate(updatedEmployee, dirtyInfo);
    },
    [setEmployee, loadPerson, validate, employee, dirtyInfo]
  );

  const handleOpenEditEmployee = useCallback(
    employee => {
      setEmployee(employee);
      setShowModal(true);
      resetEmployeeState();
    },
    [setEmployee, resetEmployeeState]
  );

  const handleCloseEmployeeModal = useCallback(() => {
    setShowModal(false);
    setEmployee({});
    resetValidation();
    resetEmployeeState();
  }, [setEmployee, resetValidation, resetEmployeeState]);

  const handleAddEmployee = useCallback(() => {
    setEmployee(emptyEmployee);
    resetEmployeeState();
    setShowModal(true);
  }, [setEmployee, resetEmployeeState]);

  const handleDeleteEmployee = useCallback(
    item => {
      setEmployee(item);
      setSaving(true);
      deleteEmployee({
        variables: { input: { partnerId: partnerId, employeeId: item.employeeId } }
      });
    },
    [deleteEmployee, setSaving, setEmployee, partnerId]
  );

  const handleSaveEmployee = useCallback(
    updatedEmployee => {
      if (!validate(employee)) return;
      setEmployee(updatedEmployee);
      setSaving(true);
      saveEmployee({
        variables: {
          input: {
            partnerId: partnerId,
            employee: {
              employeeId: employee.employeeId,
              hireDate: employee.hireDate,
              badge: employee.badge,
              reportsToId: employee.reportsToId,
              address: employee.address,
              employeeStateId: employee.employeeStateId,
              departmentId: employee.departmentId,
              person: {
                partnerId: employee.person.partnerId,
                lastName: employee.person.lastName,
                firstName: employee.person.firstName,
                genderCode: employee.person.genderCode
              }
            }
          }
        }
      });
    },
    [validate, saveEmployee, setSaving, setEmployee, partnerId, employee]
  );

  return (
    <>
      <EmployeesComponent
        saving={saving}
        hasWritePermission={hasWritePermission}
        employees={employees}
        onOpenEditEmployee={handleOpenEditEmployee}
        onAddEmployee={handleAddEmployee}
        onDeleteEmployee={handleDeleteEmployee}
      />
      <EmployeeModalComponent
        showModal={showModal}
        saving={saving}
        hasWritePermission={hasWritePermission}
        isDirty={isDirty}
        employee={employee}
        employees={employees}
        validation={validation}
        loadPersons={loadPersons}
        loadPartnerStates={loadPartnerStates}
        loadDepartments={loadDepartments}
        onPropertyChange={handlePropertyChange}
        onCloseEmployeeModal={handleCloseEmployeeModal}
        onSaveEmployee={handleSaveEmployee}
      />
    </>
  );
};

EmployeesContainer.propTypes = {
  partnerId: PropTypes.number.isRequired,
  hasWritePermission: PropTypes.bool.isRequired
};

export default EmployeesContainer;
